import { useEffect, useState } from 'react';
import { startPitchDetector, freqToNote } from './pitchDetector';

interface Props {
  /** User clicked "apply" — the panel hands back a { pitchClass, octave, cents }
   *  quantized by the user's microtone toggle, applied to the currently
   *  "armed" string (chosen from the list above). */
  onCapture: (payload: { pitchClass: string; octave: number; cents: number }) => void;
  /** Index of the string currently selected to receive the next capture. */
  armedStringIndex: number | null;
}

/**
 * Microphone "sing-to-it" mode.
 *   1. User toggles the mic ON (browser permission prompt the first time).
 *   2. A live readout shows the detected pitch (note + cents).
 *   3. User selects a string from the list (sets it as "armed").
 *   4. User clicks "Capture" while sustaining the desired pitch.
 *      The armed string is retuned to the captured pitch, either rounded
 *      to the nearest semitone or keeping the exact cents, per the toggle.
 */
export function MicMode({ onCapture, armedStringIndex }: Props) {
  const [on, setOn] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [freq, setFreq] = useState<number | null>(null);
  const [microtone, setMicrotone] = useState(false);

  // Mic lifecycle. While the mic toggle is ON, the detector is stopped
  // whenever the page is hidden (tab switch / iPad screen-lock) so it
  // doesn't keep draining battery, and re-acquired automatically on
  // return. The toggle staying ON is the manual fallback: if auto
  // re-acquire ever fails, the user just flips it off and on again.
  useEffect(() => {
    if (!on) {
      setFreq(null);
      return;
    }
    let handle: Awaited<ReturnType<typeof startPitchDetector>> | null = null;
    let cancelled = false;
    let starting = false;

    const start = async () => {
      if (handle || starting || cancelled || document.hidden) return;
      starting = true;
      try {
        const h = await startPitchDetector(
          (r) => {
            if (!cancelled) setFreq(r?.freq ?? null);
          },
          () => {
            handle = null;
          },
        );
        if (cancelled || document.hidden) {
          h.stop();
          return;
        }
        handle = h;
      } catch (e) {
        setErr(String((e as Error)?.message ?? e));
        setOn(false);
      } finally {
        starting = false;
      }
    };
    const stop = () => {
      handle?.stop();
      handle = null;
      setFreq(null);
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else void start();
    };
    const onHide = () => stop();
    const onShow = () => {
      if (!document.hidden) void start();
    };

    void start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    window.addEventListener('blur', onHide);
    window.addEventListener('focus', onShow);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('blur', onHide);
      window.removeEventListener('focus', onShow);
      stop();
    };
  }, [on]);

  // Screen Wake Lock while the mic is ON — iPad blanks the screen
  // during sustained tuning because mic capture doesn't count as user
  // activity. iOS auto-releases on tab hide, so re-acquire on return.
  useEffect(() => {
    if (!on) return;
    const wl = navigator.wakeLock;
    if (!wl) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.hidden || sentinel) return;
      try {
        sentinel = await wl.request('screen');
        sentinel.addEventListener('release', () => {
          sentinel = null;
        });
      } catch {
        // Feature/permission denied — silently skip.
      }
    };
    const onVisibility = () => {
      if (!document.hidden) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [on]);

  const note = freq ? freqToNote(freq) : null;
  const canCapture = freq !== null && armedStringIndex !== null;

  const capture = () => {
    if (!freq) return;
    const n = freqToNote(freq);
    if (microtone) {
      onCapture(n);
    } else {
      onCapture({ pitchClass: n.pitchClass, octave: n.octave, cents: 0 });
    }
  };

  return (
    <div className="mic-mode">
      <div className="mic-mode__head">
        <label className="mic-mode__toggle">
          <input
            type="checkbox"
            checked={on}
            onChange={(e) => {
              setErr(null);
              setOn(e.target.checked);
            }}
          />
          <span>Microphone ON</span>
        </label>
        <label className="mic-mode__quant">
          <input
            type="checkbox"
            checked={microtone}
            onChange={(e) => setMicrotone(e.target.checked)}
            disabled={!on}
          />
          <span>Microtone (keep cents)</span>
        </label>
      </div>

      {err && <div className="mic-mode__err">Mic error: {err}</div>}

      {on && (
        <div className="mic-mode__readout">
          {note ? (
            <>
              <div className="mic-mode__note">
                {note.pitchClass}
                {note.octave}
                {note.cents !== 0 && (
                  <span className="mic-mode__cents">
                    {' '}
                    {note.cents > 0 ? '+' : ''}
                    {note.cents}¢
                  </span>
                )}
              </div>
              <div className="mic-mode__freq">{freq!.toFixed(1)} Hz</div>
            </>
          ) : (
            <div className="mic-mode__listen">Listening… play or sing a note.</div>
          )}
        </div>
      )}

      <div className="mic-mode__actions">
        <div className="mic-mode__armed">
          {armedStringIndex !== null
            ? `Armed: string ${armedStringIndex + 1}`
            : 'Click a string in the list above to arm it.'}
        </div>
        <button
          type="button"
          className="mic-mode__capture"
          onClick={capture}
          disabled={!canCapture}
        >
          Capture → string {armedStringIndex !== null ? armedStringIndex + 1 : '—'}
        </button>
      </div>
    </div>
  );
}
